import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { CheckboxField } from '@/components/ui/checkbox-field';
import { LoadingButton } from '../../common/LoadingButton';
import type { MetadataReExtractionState } from '@hooks/useMetadataReExtraction';

interface MetadataReExtractPanelProps {
  extraction: MetadataReExtractionState;
}

// No background tint by design: pre-port `info.lighter` was never theme-defined, so only the border ever showed.
// Deliberately not on RHF+Zod: action panel, no owned/validated fields; "pick at least one" is
// the disabled action (behaviour pinned in __tests__/MetadataReExtractPanel.test.tsx).
const MetadataReExtractPanel: React.FC<MetadataReExtractPanelProps> = ({ extraction }) => {
  const { t } = useTranslation();

  return (
    <div className="mt-8 rounded-lg border border-primary/30 p-4">
      <h3 className="revy-cjk mb-2 flex items-center gap-2 text-base font-medium leading-[1.5]">
        <Sparkles className="size-5 text-primary" strokeWidth={1.9} />
        {t('documents.reExtractMetadata')}
      </h3>
      <p className="revy-cjk mb-4 text-sm leading-[1.6] text-muted-foreground">
        {t('documents.reExtractMetadataDescription')}
      </p>

      <div className="mb-4 flex flex-col gap-2">
        <CheckboxField
          id="reextract-title"
          checked={extraction.extractTitle}
          onChange={extraction.setExtractTitle}
          label={t('documents.regenerateTitle')}
        />
        <CheckboxField
          id="reextract-summary"
          checked={extraction.extractSummary}
          onChange={extraction.setExtractSummary}
          label={t('documents.regenerateSummary')}
        />
        <CheckboxField
          id="reextract-tags"
          checked={extraction.extractTags}
          onChange={extraction.setExtractTags}
          label={t('documents.regenerateTags')}
        />
      </div>

      <LoadingButton
        loading={extraction.reExtracting}
        loadingText={t('documents.reExtractingMetadata')}
        disabled={extraction.nothingSelected}
        onClick={extraction.reExtract}
      >
        <Sparkles strokeWidth={1.9} />
        <span className="revy-cjk">{t('documents.reExtractMetadata')}</span>
      </LoadingButton>
    </div>
  );
};

export default MetadataReExtractPanel;
